<template>
  <ion-page>
    <ion-header class="ion-no-border premium-header">
      <ion-toolbar>
        <ion-title class="main-title">QuickScreen</ion-title>
      </ion-toolbar>
    </ion-header>
    
    <ion-content class="premium-content">
      <div class="content-container">
        <div class="check-in-card">
          <h1 class="page-title">Welcome</h1>
          <p class="subtitle">Please enter customer information below</p>
          
          <div v-if="isPlatformWeb" id="reader" :style="{ display: isScanning ? 'block' : 'none' }"></div>
          
          <form @submit.prevent="submitCheckIn" class="check-in-form">
            <!-- Personal Information Section -->
            <div class="form-section">
              <h2 class="section-title">Personal Information</h2>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.first_name" 
                  placeholder="First Name"
                  required
                  class="premium-input"
                ></ion-input>
              </ion-item>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.last_name" 
                  placeholder="Last Name"
                  required
                  class="premium-input"
                ></ion-input>
              </ion-item>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.phone" 
                  type="tel" 
                  placeholder="Phone Number"
                  required
                  class="premium-input"
                ></ion-input>
              </ion-item>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.email" 
                  type="email" 
                  placeholder="Email Address"
                  required
                  class="premium-input"
                ></ion-input>
              </ion-item>
            </div>

            <!-- Address Section -->
            <div class="form-section">
              <h2 class="section-title">Address</h2>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.address" 
                  placeholder="Street Address"
                  required
                  class="premium-input"
                ></ion-input>
              </ion-item>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.city" 
                  placeholder="City"
                  required
                  class="premium-input"
                ></ion-input>
              </ion-item>
              <div class="state-zip-row">
                <ion-item class="form-item state-input">
                  <ion-input 
                    v-model="customer.state" 
                    placeholder="State"
                    type="text"
                    :maxlength="2"
                    @input="formatState"
                    pattern="[A-Z]{2}"
                    required
                    class="premium-input"
                  ></ion-input>
                </ion-item>
                <ion-item class="form-item zip-input">
                  <ion-input 
                    v-model="customer.zip" 
                    placeholder="ZIP"
                    type="text" 
                    :maxlength="5"
                    pattern="[0-9]*"
                    required
                    class="premium-input"
                  ></ion-input>
                </ion-item>
              </div>
            </div>

            <!-- Vehicle Information Section -->
            <div class="form-section">
              <h2 class="section-title">Vehicle Information</h2>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.plate" 
                  placeholder="License Plate (optional)"
                  class="premium-input"
                ></ion-input>
              </ion-item>
              <ion-item class="form-item">
                <ion-input 
                  v-model="customer.vin" 
                  placeholder="VIN (required)"
                  required
                  class="premium-input"
                  :maxlength="17"
                  pattern="[A-HJ-NPR-Z0-9]{17}"
                ></ion-input>
              </ion-item>
              <ion-item class="form-item">
                <ion-input 
                  v-model.number="customer.mileage" 
                  type="number" 
                  placeholder="Mileage"
                  class="premium-input"
                ></ion-input>
              </ion-item>
            </div>



            <div class="terms-container">
              <ion-item lines="none" class="terms-checkbox-item">
                <ion-checkbox v-model="agreeToTerms" mode="md" class="terms-checkbox"></ion-checkbox>
                <span class="terms-text">I agree to the <a @click="openTerms" class="terms-link">Terms and Conditions</a></span>
              </ion-item>
            </div>

            <ion-modal
              :is-open="showTerms"
              @didDismiss="closeTerms"
              class="terms-modal"
            >
              <ion-header>
                <ion-toolbar>
                  <ion-title>Terms and Conditions</ion-title>
                  <ion-buttons slot="end">
                    <ion-button @click="closeTerms">Close</ion-button>
                  </ion-buttons>
                </ion-toolbar>
              </ion-header>
              <ion-content class="terms-content">
                <div class="terms-container">
                  <h1>Terms and Conditions</h1>
                  <p><strong>Last Updated: 01/13/2025</strong></p>

                  <p>Welcome to Grip, a platform designed to help users aggregate auto data and access important financial information about their vehicles. By using our services, you agree to these Terms and Conditions, which outline the scope of the services we provide, the data we collect, how we protect it, and your rights as a user.</p>

                  <p><strong>Please read these Terms and Conditions carefully. If you do not agree with any of these terms, you should not use our services.</strong></p>

                  <h2>1. Introduction</h2>
                  <p>These Terms and Conditions ("Terms") govern your use of the Grip platform, including any associated software, mobile applications, services, and data provided (collectively referred to as the "Services"). By accessing or using Grip, you agree to be bound by these Terms.</p>

                  <h2>2. Data Collection and Consent</h2>
                  <p>As part of our services, we collect and process certain personal information, including but not limited to:</p>
                  <ul>
                    <li>Your credit information (via a soft credit pull)</li>
                    <li>Car valuation data (including details about the make, model, year, and current market value)</li>
                    <li>Additional personal and financial information you voluntarily provide, including but not limited to income, loan details, and related data</li>
                  </ul>

                  <p>By using our services, you explicitly agree to:</p>
                  <ul>
                    <li>Consent to the soft pull of your credit report. Soft credit inquiries will not impact your credit score but are used to verify your identity and provide accurate financial insights.</li>
                    <li>Consent to the retrieval and use of car valuation data, which includes vehicle information sourced from third-party databases to provide accurate market valuations.</li>
                  </ul>

                  <p>Grip is committed to using your data in compliance with applicable laws and regulations, including those governing credit reporting and consumer privacy.</p>

                  <h2>3. Soft Credit Pull Disclaimer</h2>
                  <p>We perform soft credit pulls to obtain credit-related information, which helps us assess your financial situation for the purpose of offering auto loan insights and vehicle data. This soft pull will not affect your credit score. However, you understand and agree that:</p>
                  <ul>
                    <li>You are providing explicit consent for us to perform a soft inquiry on your credit report.</li>
                    <li>The credit report information retrieved is limited in scope and used solely for providing the services offered by Grip.</li>
                  </ul>

                  <h2>4. Car Valuation Data</h2>
                  <p>We use your vehicle information to retrieve market valuation data from third-party sources to provide you with up-to-date and accurate insights about the value of your car. While we strive for accuracy, market fluctuations and data variances may result in slightly different valuations. You agree that:</p>
                  <ul>
                    <li>We are not liable for any discrepancies in the car valuation data provided.</li>
                    <li>You are responsible for verifying the accuracy of any information before making financial decisions based on the valuation data.</li>
                  </ul>

                  <h2>5. Privacy Policy</h2>
                  <p>Our Privacy Policy outlines how we handle and protect your personal information, including credit and vehicle data. By using our Services, you agree to the collection, use, and sharing of your data as described in our Privacy Policy. We take your privacy seriously and employ industry-standard security measures to protect your information.</p>

                  <h2>6. No Liability for Third-Party Information</h2>
                  <p>Our platform may use third-party databases, services, or APIs to retrieve credit and car valuation data. We are not responsible for any inaccuracies or issues that arise from third-party information. You understand and agree that:</p>
                  <ul>
                    <li>Any information retrieved from third-party sources is provided "as is" and without any warranties of accuracy.</li>
                    <li>We are not liable for any decisions you make based on third-party data.</li>
                  </ul>

                  <h2>7. Use of Services</h2>
                  <p>Your use of the Services is for personal and non-commercial purposes only. You may not resell, sublicense, or otherwise distribute our services or the data we provide without prior written consent.</p>
                  <p>You agree to use the Services in a manner that is legal, ethical, and in compliance with all applicable laws and regulations, including but not limited to those related to data privacy, credit reporting, and consumer protection.</p>

                  <h2>8. Limitations of Liability</h2>
                  <p>To the fullest extent permitted by applicable law, Grip and its owners, officers, employees, or partners are not liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from:</p>
                  <ul>
                    <li>Your use of our Services</li>
                    <li>Any inaccuracies or omissions in the data we provide</li>
                    <li>Any unauthorized access or use of our platform</li>
                  </ul>
                  <p>Grip provides its Services "as is" without any warranties, express or implied. We make no guarantees regarding the accuracy or completeness of the information provided.</p>

                  <h2>9. Indemnification</h2>
                  <p>You agree to indemnify, defend, and hold harmless Grip and its affiliates, directors, officers, employees, and agents from any claims, damages, liabilities, costs, or expenses (including legal fees) arising out of:</p>
                  <ul>
                    <li>Your breach of these Terms</li>
                    <li>Your use of the Services</li>
                    <li>Any legal claims or actions brought against Grip related to your use of the Services</li>
                  </ul>

                  <h2>10. Changes to Terms</h2>
                  <p>We may modify these Terms at any time. Any changes will be effective immediately upon posting. It is your responsibility to regularly check these Terms for updates. Your continued use of our Services after the posting of any changes constitutes your acceptance of the modified Terms.</p>

                  <h2>11. Governing Law</h2>
                  <p>These Terms are governed by and construed in accordance with the laws of Delaware, USA, without regard to its conflict of law principles. Any legal action arising from these Terms shall be filed exclusively in the courts of Delaware, USA.</p>

                  <h2>12. Contact Information</h2>
                  <p>If you have any questions about these Terms or your rights as a user, please contact us at:</p>
                  <p>Email: info@gripintelligence.com</p>

                  <h2>13. Electronic Disclosures and Consent</h2>
                  <h3>Electronic Signatures in Global and National Commerce Act</h3>
                  <p>Introduction: You are submitting a request for a loan pre-qualification, a loan application or form through us (hereinafter, a "Request"). We can only give you the benefits of our service by conducting our business through the Internet. In order to do this, we need you to consent to our giving you certain Disclosures electronically. This document informs you of your rights when receiving legally required disclosures, notices and information ("Disclosures") from us and Grip Intelligence Request is submitted. By consenting, you acknowledge receipt of this document and agree to receive all Disclosures required by law solely in electronic form. The electronic delivery of such Disclosures.</p>

                  <h3>Electronic Communications</h3>
                  <p>Any Disclosures related to your Request will be provided to you electronically through our technology provider, Grip Intelligence. However, if you wish to obtain a paper copy of any of the Disclosures, you may write to us at: info@gripintelligence.com with the details of your request. Paper copies will be provided to you at no charge.</p>

                  <h3>Consenting to Do Business Electronically</h3>
                  <p>Before you decide to do business electronically with us, you should consider whether you have the required hardware and software capabilities described below.</p>

                  <h3>Scope of Consent</h3>
                  <p>Your consent to receive Disclosures and to do business electronically, and our agreement to do so, only applies to this Request. You will receive Disclosures from us and our technology provider, Grip Intelligence. If you choose to apply for other products or services through us, you may be required to consent to additional Disclosures electronically.</p>

                  <h3>Hardware and Software Requirements</h3>
                  <p>To access and retain the Disclosures electronically, you will need to use the following computer software and hardware: Internet Explorer 11 or Edge, Firefox, Chrome and Safari. or equivalent software; and hardware capable of running this software. If we revise the software or hardware requirements necessary to access and retain the Disclosures electronically within 30 days of our initial Disclosures to you, we will advise you of the new requirements.</p>

                  <h3>Withdrawing Consent</h3>
                  <p>Because we will provide the Disclosures to you instantaneously, you will not be able to withdraw your consent to do business electronically with us. However, you may withdraw your consent to do business electronically with our participating Lenders at no cost to you. You may do so by contacting the Lender at the mailing address, e-mail address or telephone number they provide with their offer. If you decide to withdraw your consent, the legal validity and enforceability of prior electronic Disclosures will not be affected.</p>

                  <h3>Changes to Your Contact Information</h3>
                  <p>You should keep us informed of any change in your electronic or mailing address. You may contact Customer Care by email at info@gripintelligence.com regarding any such changes.</p>

                  <h3>Your Ability to Access Disclosures</h3>
                  <p>By completing and submitting your request, you acknowledge that you can access the electronic disclosures in the designated formats described above.</p>

                  <h3>Consent</h3>
                  <p>By completing and submitting your request, you consent to having all disclosures provided or made available to you in electronic form and to doing business with us electronically.</p>

                  <p><strong>You understand that by clicking the I Agree Checkbox and Submit, you acknowledge having read Grip Intelligence's Privacy Policy Notice and you are providing 'written instructions' to Grip Intelligence under the Fair Credit Reporting Act to have Grip Intelligence have my credit file accessed for purposes of prequalifying for a loan. You authorize Grip Intelligence to obtain such information solely to conduct a prequalification for credit. Also, you authorize Grip Intelligence to share the information provided in this application to affiliated third parties for possible financing options.</strong></p>

                  <p><strong>By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.</strong></p>
                </div>
              </ion-content>
            </ion-modal>

            <ion-button 
              type="submit" 
              expand="block" 
              class="submit-button premium-button" 
              :disabled="!agreeToTerms"
            >
              Submit
            </ion-button>
          </form>
        </div>
      </div>
      
      <UpgradeOverlay
        v-if="showUpgradeOverlay"
        message="Instant, on-demand soft pull and GRIP feature access. For activation, contact us at info@gripintelligence.com"
      />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, onUnmounted, onMounted, watch } from 'vue';
import { api } from '@/services/api';
import { cache } from '@/services/cache.service';
import { 
  IonPage, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonInput, 
  IonButton, 
  IonCheckbox, 
  IonCard, 
  IonCardHeader, 
  IonCardTitle, 
  IonCardContent,
  loadingController,
  toastController
} from '@ionic/vue';
import { useRouter, onBeforeRouteLeave, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { useNotifications } from '@/store/notifications'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Capacitor } from '@capacitor/core';
import { scanOutline, barcodeOutline } from 'ionicons/icons'
// Conditionally import BarcodeScanner only for iOS
let BarcodeScanner: any = {};
if (Capacitor.getPlatform() === 'ios') {
  import('@awesome-cordova-plugins/barcode-scanner').then(module => {
    BarcodeScanner = module.BarcodeScanner;
  });
}
import { useUserStore } from '@/store/user';
import { IonNav } from '@ionic/vue';
import { Notification } from '@/store/notifications';
import { supabase } from '@/supabase';
import { trackUserActivity } from '@/services/tracking';
import { useToast } from '@/composables/useToast'
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import UpgradeOverlay from '@/components/UpgradeOverlay.vue';

// Add router at the top with other refs
const router = useRouter();

// Form data structure for customer check-in
interface CustomerCheckIn {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  plate: string;
  vin: string;
  mileage: string;
  address: string;
  zip: string;
  status: boolean;
}

interface CustomerCheckInRequest extends CustomerCheckIn {
  rooftop_id: string;
  engaged: boolean;
}

const defaultCustomer: CustomerCheckIn = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  city: '',
  state: '',
  plate: '',
  vin: '',
  mileage: '',
  address: '',
  zip: '',
  status: false
};

const customer = ref<CustomerCheckIn>(defaultCustomer);

// State management for terms and conditions
const agreeToTerms = ref(false);
const showTerms = ref(false);
const scanningInProgress = ref(false);
const isScanning = ref(false);
const isPlatformWeb = ref(Capacitor.getPlatform() === 'web');
const showUpgradeOverlay = ref(false);
const dealerQuickScreenEnabled = ref(false);

const { addNotification } = useNotifications()

const userStore = useUserStore(); // Access the user store

const { showToast } = useToast()

// Add storage utility functions
const Storage = {
  async get(key: string): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },

  async remove(key: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }
};

// Add after imports
const STORAGE_KEY = 'customer_checkin_draft';

// Update the storage functions
const loadSavedFormData = async () => {
  try {
    const savedData = await Storage.get(STORAGE_KEY);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      customer.value = { ...defaultCustomer, ...parsedData };
    }
  } catch (e) {
    console.error('Error loading saved form data:', e);
    customer.value = { ...defaultCustomer };
  }
};

const saveFormData = async () => {
  try {
    await Storage.set(STORAGE_KEY, JSON.stringify(customer.value));
  } catch (e) {
    console.error('Error saving form data:', e);
  }
};

// Update the watch handler
watch(
  () => customer.value,
  async () => {
    await saveFormData();
  },
  { deep: true }
);

// Update the navigation guard with proper types
onBeforeRouteLeave((
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  saveFormData();
  next();
});

// Update onMounted
onMounted(async () => {
  try {
    // Get current session
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    // Ensure user store is initialized
    if (currentSession?.user) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentSession.user.id)
        .single();

      if (!profileError && profile) {
        await userStore.setUser({
          id: profile.id,
          username: profile.username,
          isAdmin: profile.is_admin,
          isSuperAdmin: profile.is_super_admin || false,
          isOwner: profile.is_owner || false,
          rooftop_id: profile.rooftop_id,
          full_name: '',
          avatar_url: '',
          updated_at: ''
        });
      }
    }

    // Set up app state change listener
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        // Reinitialize user data when app becomes active
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession?.user) {
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', currentSession.user.id)
              .single();

            if (!profileError && profile) {
              await userStore.setUser({
                id: profile.id,
                username: profile.username,
                isAdmin: profile.is_admin,
                isSuperAdmin: profile.is_super_admin || false,
                isOwner: profile.is_owner || false,
                rooftop_id: profile.rooftop_id,
                full_name: '',
                avatar_url: '',
                updated_at: ''
              });
            }
          }
          // Load saved form data after reinitializing user
          await loadSavedFormData();
        } catch (err) {
          console.error('Error reinitializing user data:', err);
        }
      }
    });

    // Check if dealer has QuickScreen access
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: dealer } = await supabase
        .from('dealers')
        .select('quick_screen')
        .eq('rooftop_id', userStore.getRooftopId.value)
        .single();
      
      console.log('Dealer data:', dealer);
      console.log('Quick screen value:', dealer?.quick_screen);
      
      // Make sure we explicitly handle the boolean value
      dealerQuickScreenEnabled.value = dealer?.quick_screen === true;
      showUpgradeOverlay.value = dealer?.quick_screen !== true;
      
      console.log('dealerQuickScreenEnabled:', dealerQuickScreenEnabled.value);
      console.log('showUpgradeOverlay:', showUpgradeOverlay.value);
    }

  } catch (err) {
    console.error('Error in mounted:', err);
  }
});

/**
 * Opens the terms and conditions modal
 */
const openTerms = () => {
  showTerms.value = true;
};

/**
 * Closes the terms and conditions modal
 */
const closeTerms = () => {
  showTerms.value = false;
};

/**
 * Handles customer check-in form submission
 * - Validates terms agreement
 * - Submits data to API
 * - Clears form on success
 * - Shows success/error messages
 */
const submitCheckIn = async () => {
  const loading = await loadingController.create({
    message: 'Checking in...'
  });
  await loading.present();

  try {
    // Validate VIN
    if (!customer.value.vin) {
      await showToast({
        message: 'VIN is required',
        color: 'danger'
      });
      return;
    }

    // Validate VIN format (17 characters, alphanumeric, excluding I, O, Q)
    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!vinRegex.test(customer.value.vin)) {
      await showToast({
        message: 'Please enter a valid 17-character VIN',
        color: 'danger'
      });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    const rooftopId = userStore.getRooftopId.value;
    if (!rooftopId) {
      throw new Error('No rooftop ID found');
    }

    const userId = userStore.user.value?.id;
    if (!userId) {
      throw new Error('No user ID found');
    }

    // First update the customer data directly
    const { data: newCustomer, error: customerError } = await supabase
      .from('customer_data')
      .insert([
        {
          ...customer.value,
          rooftop_id: rooftopId,
          engaged: true,
          created_at: new Date().toISOString(),
          vehicle_data_status: 'pending'
        }
      ])
      .select()
      .single();

    if (customerError) {
      throw customerError;
    }

    if (!newCustomer) {
      throw new Error('Failed to create customer');
    }

    console.log('Check-in successful, customer ID:', newCustomer.id);

    // Store customer info before clearing
    const customerName = `${customer.value.first_name} ${customer.value.last_name}`;

    // Create notification
    try {
      // Get user's profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw profileError;
      }

      // Create check-in notification
      await createCheckInNotification({
        ...customer.value,
        id: newCustomer.id
      }, userId, rooftopId);

      await trackUserActivity(
        'customer_checkin',
        userId,
        rooftopId,
        newCustomer.id,
        {
          customer_name: customerName,
          plate: customer.value.plate
        }
      );

      // Send request to Pipedream webhook for opt-in text
      try {
        console.log('Starting webhook request process...');
        
        // Get dealer information
        console.log('Fetching dealer info for rooftop_id:', rooftopId);
        const { data: dealerData, error: dealerError } = await supabase
          .from('dealers')
          .select('dealer_name')
          .eq('rooftop_id', rooftopId)
          .single();

        console.log('Dealer data:', dealerData);
        console.log('Dealer error:', dealerError);

        if (dealerError) {
          console.error('Error fetching dealer:', dealerError);
          throw dealerError;
        }

        const requestBody = {
          phone: customer.value.phone,
          name: customer.value.first_name && customer.value.last_name ? `${customer.value.first_name} ${customer.value.last_name}` : '',
          action: 'send_text',
          dealer_name: dealerData?.dealer_name || ''
        };
        
        console.log('Preparing to send webhook with body:', requestBody);

        const webhookResponse = await fetch(import.meta.env.VITE_TEXT_MESSAGE_WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        console.log('Webhook response status:', webhookResponse.status);
        const responseText = await webhookResponse.text();
        console.log('Webhook response:', responseText);

        if (!webhookResponse.ok) {
          console.error('Failed to send webhook for opt-in text:', webhookResponse.statusText);
        } else {
          console.log('Successfully sent opt-in text webhook');
        }
      } catch (webhookError: any) {
        console.error('Error sending opt-in text webhook:', webhookError);
        console.error('Error details:', {
          message: webhookError?.message,
          stack: webhookError?.stack,
          cause: webhookError?.cause
        });
      }

      // Update the cleanup to use unified Storage
      await Storage.remove(STORAGE_KEY);
      customer.value = { ...defaultCustomer };
      agreeToTerms.value = false;
      await clearForm();

      await showToast({
        message: 'Check-in successful',
        color: 'success'
      });

      // Navigate to Tab1 after successful check-in
      router.push('/tabs/tab1');
    } catch (error) {
      console.error('Error during check-in:', error);
      await showToast({
        message: 'Error during check-in. Please try again.',
        color: 'danger'
      });
    }
  } catch (error) {
    console.error('Error during check-in:', error);
    await showToast({
      message: 'Error during check-in. Please try again.',
      color: 'danger'
    });
  } finally {
    await loading.dismiss();
  }
};

/**
 * Handles scanning the driver's license barcode
 */
// Scan ID function removed as it's not needed in web version
// const scanID = async () => {
//   try {
//     // Function implementation removed
//   } catch (error) {
//     console.error('Scan error:', error);
//   } finally {
//     scanningInProgress.value = false;
//   }
// };

/**
 * Parses the driver's license barcode data
 * @param rawValue - The raw barcode data
 * @returns The parsed driver's license data
 */
const parseDriversLicense = (rawValue: string) => {
  // AAMVA format parsing
  const fields: { [key: string]: string } = {}
  
  // Split the data into segments
  const segments = rawValue.split('\n')
  
  for (const segment of segments) {
    if (segment.startsWith('DAC')) { // First Name
      fields.first_name = segment.substring(3).trim()
    }
    if (segment.startsWith('DCS')) { // Last Name
      fields.last_name = segment.substring(3).trim()
    }
    if (segment.startsWith('DAG')) { // Street Address
      fields.address = segment.substring(3).trim()
    }
    if (segment.startsWith('DAI')) { // City
      fields.city = segment.substring(3).trim()
    }
    if (segment.startsWith('DAJ')) { // State
      fields.state = segment.substring(3).trim()
    }
    if (segment.startsWith('DAK')) { // ZIP
      fields.zip = segment.substring(3).trim().slice(0, 5);
    }
  }

  return fields
}

// Add this function before submitCheckIn
const formatState = (event: any) => {
  if (event.target) {
    customer.value.state = event.target.value.toUpperCase();
  }
};

// Cleanup on unmount
onUnmounted(() => {
  if (isScanning.value && isPlatformWeb.value) {
    const html5QrCode = new Html5Qrcode("reader");
    html5QrCode.stop().catch(console.error);
    isScanning.value = false;
  }
});

const createCheckInNotification = async (customer: any, userId: string, rooftopId: string) => {
  const { addNotification } = useNotifications()
  
  try {
    // Create check-in notification
    await addNotification({
      type: 'checkin',
      user_id: userId,
      rooftop_id: rooftopId,
      customer_id: customer.id,
      message: `New check-in: ${customer.first_name} ${customer.last_name}`,
      status: 'new',
      engaged_by: userId
    });

    console.log('Check-in notification created successfully for customer:', customer.id);
  } catch (error) {
    console.error('Error creating notification:', error)
    throw error
  }
}

// Gone button functionality removed

// Add VIN scanning function
const scanVIN = async () => {
  try {
    scanningInProgress.value = true;
    
    if (isPlatformWeb.value) {
      await showToast({
        message: 'VIN scanning is only available in the iOS mobile app. Please enter VIN manually.',
        color: 'warning'
      });
      scanningInProgress.value = false;
      return;
    } else if (Capacitor.getPlatform() !== 'ios') {
      // Show toast message for Android users
      await showToast({
        message: 'VIN scanning is only available on iOS devices. Please enter VIN manually.',
        color: 'warning'
      });
      scanningInProgress.value = false;
      return;
    } else {
      const barcodeData = await BarcodeScanner.scan({
        preferFrontCamera: false,
        showFlipCameraButton: true,
        showTorchButton: true,
        formats: "CODE_39,CODE_128,PDF_417",
        prompt: "Position the barcode within the scanning area",
        resultDisplayDuration: 500,
        orientation: "landscape",
        disableSuccessBeep: true
      });
      
      if (barcodeData.text) {
        // Clean and validate VIN
        const vin = barcodeData.text.replace(/[^A-Z0-9]/gi, '').toUpperCase();
        if (vin.length === 17) {
          customer.value.vin = vin;
          await showToast({
            message: 'VIN scanned successfully!',
            color: 'success'
          });
        } else {
          throw new Error('Invalid VIN format');
        }
      }
    }
  } catch (error) {
    console.error('VIN scan error:', error);
    await showToast({
      message: error instanceof Error ? error.message : 'Failed to scan VIN. Please try again or enter manually.',
      color: 'danger'
    });
  } finally {
    scanningInProgress.value = false;
  }
};

// Clear form function
const clearForm = async () => {
  customer.value = { ...defaultCustomer };
  await Storage.remove(STORAGE_KEY);
};
</script>

<style scoped>
.premium-header {
  --background: #FFFFFF;
}

.premium-header ion-toolbar {
  --background: #FFFFFF;
  --color: #000000;
  --border-style: none;
}

.main-title {
  font-size: 28px;
  font-weight: 700;
  color: #000000;
  text-align: center;
}

.premium-content {
  --background: #F8F9FA;
  background: #F8F9FA;
}

ion-page {
  display: flex;
  flex-direction: column;
  background: #F8F9FA;
}

ion-content {
  --overflow: auto;
  --padding-bottom: 20px;
  --background: #F8F9FA;
}

ion-content::part(scroll) {
  background: #F8F9FA;
}

.content-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 64px 16px 80px;
  background: #F8F9FA;
}

.check-in-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #E5E5EA;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 16px;
  color: #666666;
  margin: 0 0 24px 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin: 24px 0 16px 0;
}

.form-section {
  margin-bottom: 24px;
}

.form-item {
  --background: #F2F2F7;
  --border-color: transparent;
  --border-radius: 12px;
  --padding-start: 16px;
  --padding-end: 16px;
  margin-bottom: 16px;
}

.form-item:last-child {
  margin-bottom: 0;
}

.premium-input {
  --color: #000000;
  --placeholder-color: #666666;
  font-size: 16px;
}

.state-zip-row {
  display: flex;
  gap: 16px;
}

.state-input {
  flex: 1;
}

.zip-input {
  flex: 2;
}

.premium-button {
  --background: #1C1C1E;
  --background-activated: #2C2C2E;
  --background-hover: #2C2C2E;
  --color: #FFFFFF;
  --border-radius: 10px;
  --box-shadow: none;
  font-weight: 500;
  margin: 24px 0;
  height: 44px;
  font-size: 15px;
  letter-spacing: -0.2px;
}

.premium-button ion-icon {
  font-size: 18px;
  margin-right: 6px;
  opacity: 0.9;
}

.scan-button {
  margin-bottom: 32px;
}

.terms-container {
  margin: 24px 0;
  display: flex;
  justify-content: center;
}

.terms-checkbox-item {
  --background: transparent;
  --border-color: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --min-height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.terms-checkbox {
  --background-checked: #F2F2F7;
  --border-color: #8E8E93;
  --border-color-checked: #8E8E93;
  --checkmark-color: #000000;
  --checkbox-background-checked: #F2F2F7;
  margin-right: 4px;
  --ion-color-primary: #F2F2F7;
  --ion-color-primary-contrast: #000000;
}

.terms-checkbox::part(container) {
  background: transparent;
  border-color: #8E8E93;
}

.terms-checkbox::part(mark) {
  background: #F2F2F7;
}

.terms-label {
  color: #666666;
  font-size: 14px;
}

.terms-link {
  color: #0A84FF;
  text-decoration: none;
}

.terms-modal {
  --height: 95%;
  --border-radius: 16px;
  --width: 95%;
  --max-width: 800px;
  --background: #FFFFFF;
}

.terms-modal .modal-wrapper,
.terms-modal .ion-page,
.terms-modal ion-content,
.terms-modal ion-content::part(scroll),
.terms-modal ion-content::part(background),
.terms-modal::part(content) {
  background: #FFFFFF !important;
  height: 100%;
}

.terms-modal ion-header {
  --background: #FFFFFF;
}

.terms-modal ion-header ion-toolbar {
  --background: #FFFFFF;
  --color: #000000;
  --border-color: #E5E5EA;
}

.terms-modal .terms-container {
  padding: 24px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  color: #000000;
  padding-bottom: 120px;
  width: 100%;
  box-sizing: border-box;
}

.terms-modal h1 {
  font-size: 24px;
  font-weight: 600;
  color: #000000 !important;
  margin-bottom: 12px;
  letter-spacing: -0.2px;
}

.terms-modal h2 {
  font-size: 20px;
  font-weight: 600;
  color: #000000 !important;
  margin: 32px 0 16px;
  letter-spacing: -0.2px;
}

.terms-modal h3 {
  font-size: 18px;
  font-weight: 600;
  color: #000000 !important;
  margin: 24px 0 12px;
  letter-spacing: -0.2px;
}

.terms-modal p {
  font-size: 16px;
  line-height: 1.6;
  color: #000000 !important;
  margin-bottom: 16px;
  letter-spacing: -0.2px;
}

.terms-modal ul {
  margin: 16px 0;
  padding-left: 24px;
  list-style-type: disc;
}

.terms-modal li {
  font-size: 16px;
  line-height: 1.6;
  color: #000000 !important;
  margin-bottom: 12px;
  letter-spacing: -0.2px;
}

.terms-modal strong {
  color: #000000 !important;
  font-weight: 600;
}

.terms-text {
  text-align: center;
  margin: 16px 0;
  font-size: 14px;
  color: #666666;
}

/* Responsive styles */
@media (max-width: 480px) {
  .content-container {
    padding: 16px;
  }

  .check-in-card {
    padding: 16px;
  }

  .page-title {
    font-size: 22px;
  }

  .section-title {
    font-size: 16px;
  }
}

.premium-header ion-title {
  font-size: 17px;
}

.scan-buttons-container {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.scan-button {
  flex: 1;
  margin: 0;
  height: 44px;
  font-size: 14px;
}
</style>


